/**
 * PRODUCTION FIX: Error boundary for React component errors
 * Catches errors in component tree and displays fallback UI
 */

import React, { Component, ReactNode } from 'react';
import { normalizeCaughtError } from '@/shared/utils/errorHandler';
import { log } from '@/shared/utils/logger';
import i18n from '@/shared/i18n/config';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode | ((error: Error, errorInfo: React.ErrorInfo, reset: () => void) => ReactNode);
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
  resetKeys?: Array<string | number>;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

const EMPTY_ERROR_INFO: React.ErrorInfo = { componentStack: '' };


/**
 * Base ErrorBoundary component for catching React errors
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(thrownValue: unknown): Partial<ErrorBoundaryState> {
    return {
      hasError: true,
      error: normalizeCaughtError(thrownValue),
    };
  }

  componentDidCatch(thrownValue: unknown, errorInfo: React.ErrorInfo): void {
    const error = normalizeCaughtError(thrownValue);
    log.error('ErrorBoundary', 'Caught error:', error, errorInfo);

    this.setState({
      error,
      errorInfo,
    });

    // Call optional error handler
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps): void {
    const previousKeys = prevProps.resetKeys ?? [];
    const currentKeys = this.props.resetKeys ?? [];
    // Callers build key arrays inline. A new array with identical values must
    // not retry a failing child on every unrelated parent render.
    const keysChanged = previousKeys.length !== currentKeys.length ||
      previousKeys.some((key, index) => !Object.is(key, currentKeys[index]));
    if (this.state.hasError && keysChanged) {
      this.resetErrorBoundary();
    }
  }

  resetErrorBoundary = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render(): ReactNode {
    if (this.state.hasError && this.state.error) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        if (typeof this.props.fallback === 'function') {
          return this.props.fallback(
            this.state.error,
            this.state.errorInfo ?? EMPTY_ERROR_INFO,
            this.resetErrorBoundary
          );
        }
        return this.props.fallback;
      }

      // Default fallback UI
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
          <div className="max-w-md w-full bg-white dark:bg-gray-800 shadow-lg rounded-lg p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-shrink-0">
                <svg
                  className="h-6 w-6 text-red-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-zinc-900 dark:text-zinc-100">
                {i18n.t('Something went wrong')}
              </h3>
            </div>

            <p className="mb-4 text-sm text-zinc-600 dark:text-zinc-400">
              {i18n.t('An unexpected error occurred. Please try refreshing the page or contact support if the problem persists.')}
            </p>

            {this.state.error && (
              <details className="mb-4">
                <summary className="cursor-pointer text-sm font-medium text-zinc-700 dark:text-zinc-300">
                  {i18n.t('Error details')}
                </summary>
                <pre className="mt-2 max-h-40 overflow-auto rounded bg-zinc-100 p-3 text-xs text-zinc-600 dark:bg-zinc-900 dark:text-zinc-400">
                  {this.state.error.toString()}
                  {this.state.errorInfo?.componentStack}
                </pre>
              </details>
            )}

            <div className="flex gap-3">
              <button
                onClick={this.resetErrorBoundary}
                className="flex-1 rounded-md border border-zinc-300 px-4 py-2 text-sm font-medium text-zinc-700 transition-colors hover:bg-zinc-100 dark:border-zinc-700 dark:text-zinc-200 dark:hover:bg-zinc-800"
              >
                {i18n.t('Try again')}
              </button>
              <button
                onClick={() => window.location.reload()}
                className="flex-1 rounded-md border border-zinc-300 px-4 py-2 text-sm font-medium text-zinc-700 transition-colors hover:bg-zinc-100 dark:border-zinc-700 dark:text-zinc-200 dark:hover:bg-zinc-800"
              >
                {i18n.t('Reload page')}
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * Default export for convenience
 */
export default ErrorBoundary;
